import { useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import AutocompleteInput from "@/components/shared/AutocompleteInput";
import DrugSearchInput from "@/components/shared/DrugSearchInput";
import FormTable, { MedicineTableColumns } from "@/components/shared/FormTable";
import { useAuth } from "@/context/AuthContext";
import { DrugSearchResult, recordDrugUsage } from "@/lib/api";
import {
  DOSAGE_OPTIONS,
  WHEN_OPTIONS,
  DURATION_OPTIONS
} from "@/lib/constants";

const DEFAULT_ROW = {
  drug_id: null as string | null,
  brand_name: "",
  generic_name: "",
  dosage: "",
  taken_when: "",
  duration: "",
  quantity: "",
  notes: ""
};

export default function MedicineListForm() {
  const {
    control,
    register,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors }
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({ control, name: "medicines" });
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const watched = watch("medicines") || [];
  const [initialized, setInitialized] = useState(false);
  const { currentUser } = useAuth();

  // Initialize with one empty medicine if none exist
  useEffect(() => {
    if (!initialized && fields.length === 0) {
      append(DEFAULT_ROW);
      setInitialized(true);
    }
  }, [fields.length, append, initialized]);

  const handleDrugSelect = (index: number, drug: DrugSearchResult) => {
    setValue(`medicines.${index}.drug_id`, drug.id, { shouldDirty: false });
    setValue(`medicines.${index}.brand_name`, drug.brand_name, { shouldDirty: false });
    setValue(`medicines.${index}.generic_name`, drug.generic_name, { shouldDirty: false });
    // setValue(`medicines.${index}.product`, drug.brand_name, { shouldDirty: true });
    console.log("Selected drug:", drug);
    if (currentUser?.doctor_id && currentUser?.tenant_id) {
      recordDrugUsage(drug.id, currentUser.doctor_id, currentUser.tenant_id).catch(() => {});
    }
  };

  // Smart quantity calculation with improved parsing
  const handleAutoQuantity = (index: number) => {
    // Use setTimeout to ensure form values are updated
    setTimeout(() => {
      const dosage = getValues(`medicines.${index}.dosage`)?.trim() || "";
      const duration = getValues(`medicines.${index}.duration`)?.trim() || "";
      
      // Parse dosage like "1-0-1" or "2-2-2"
      const dosageParts = dosage.split("-").map((d: string) => Number.parseFloat(d) || 0);
      const dailyDoses = dosageParts.reduce((sum: number, dose: number) => sum + dose, 0);
      
      // Parse duration with proper conversion to days
      let durationDays = 0;
      const durationLower = duration.toLowerCase();
      
      if (durationLower.includes("day")) {
        // Extract number before "day" or "days"
        const match = duration.match(/(\d+)\s*days?/i);
        durationDays = match ? Number.parseInt(match[1]) : 0;
      } else if (durationLower.includes("week")) {
        // Extract number before "week" and convert to days
        const match = duration.match(/(\d+)\s*weeks?/i);
        durationDays = match ? Number.parseInt(match[1]) * 7 : 0;
      } else if (durationLower.includes("month")) {
        // Extract number before "month" and convert to days
        const match = duration.match(/(\d+)\s*months?/i);
        durationDays = match ? Number.parseInt(match[1]) * 30 : 0;
      } else if (durationLower.includes("continue") || durationLower.includes("chronic") || durationLower.includes("long term")) {
        // For long-term medications, assume 30 days supply
        durationDays = 30;
      } else if (durationLower.includes("till review") || durationLower.includes("till next visit")) {
        // For till review, assume 15 days supply
        durationDays = 15;
      } else if (durationLower.includes("complete course")) {
        // For complete course, assume 7 days
        durationDays = 7;
      } else if (durationLower.includes("as directed")) {
        // For as directed, assume 10 days
        durationDays = 10;
      } else {
        // For other formats, try to extract just the number
        const numberMatch = duration.match(/^\d+/);
        durationDays = numberMatch ? Number.parseInt(numberMatch[0]) : 0;
      }
      
      if (dailyDoses > 0 && durationDays > 0) {
        const totalQuantity = Math.ceil(dailyDoses * durationDays);
        
        setValue(`medicines.${index}.quantity`, totalQuantity.toString(), {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        });
        
        // Force form to re-render
        trigger(`medicines.${index}.quantity`);
      }
    }, 100); // Small delay to ensure form state is updated
  };

  const focusNextField = (index: number, fieldOffset: number) => {
    const nextIndex = index * 6 + fieldOffset;
    if (inputRefs.current[nextIndex]) {
      setTimeout(() => inputRefs.current[nextIndex]?.focus(), 50);
    }
  };

  // Render cell content based on column key
  const renderCell = (field: any, columnKey: string, index: number) => {
    switch (columnKey) {
      case 'medicine':
        return (
          <DrugSearchInput
            value={getValues(`medicines.${index}.product`) || null}
            onChange={(drug) => handleDrugSelect(index, drug)}
            onCustomInput={(val) => {
              setValue(`medicines.${index}.product`, val, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false
              });
              setValue(`medicines.${index}.drug_id`, null, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false
              });
            }}
            placeholder="Search medicine..."
            onEnterNext={() => focusNextField(index, 1)}
            className={cn(
              "h-7 px-2 text-xs border border-input rounded-md bg-background transition-all duration-200",
              "focus:border-ring focus:outline-none focus-visible:ring-0",
              "hover:border-ring/60 placeholder:text-muted-foreground placeholder:font-normal",
              errors.medicines?.[index]?.product && "border-destructive/30 bg-destructive/5 focus:border-destructive"
            )}
            ref={(el) => {
              inputRefs.current[index * 6] = el;
            }}
          />
        );

      case 'dosage':
        return (
          <AutocompleteInput
            value={getValues(`medicines.${index}.dosage`) || ""}
            onChange={(val) => {
              setValue(`medicines.${index}.dosage`, val, { shouldDirty: true });
              handleAutoQuantity(index);
            }}
            options={DOSAGE_OPTIONS}
            placeholder="1-0-1"
            onEnterNext={() => focusNextField(index, 2)}
            className={cn(
              "h-7 px-2 text-xs border border-input rounded-md bg-background transition-all duration-200",
              "focus:border-ring focus:outline-none focus-visible:ring-0",
              "hover:border-ring/60 placeholder:text-muted-foreground placeholder:font-normal",
              errors.medicines?.[index]?.dosage && "border-destructive/30 bg-destructive/5 focus:border-destructive"
            )}
            showAllOnFocus={true}
            dropdownMinWidth={120}
            ref={(el) => {
              inputRefs.current[index * 6 + 1] = el;
            }}
          />
        );

      case 'taken_when':
        return (
          <AutocompleteInput
            value={getValues(`medicines.${index}.taken_when`) || ""}
            onChange={(val) => setValue(`medicines.${index}.taken_when`, val, { shouldDirty: true })}
            options={WHEN_OPTIONS}
            placeholder="When"
            onEnterNext={() => focusNextField(index, 3)}
            className={cn(
              "h-7 px-2 text-xs border border-input rounded-md bg-background transition-all duration-200",
              "focus:border-ring focus:outline-none focus-visible:ring-0",
              "hover:border-ring/60 placeholder:text-muted-foreground placeholder:font-normal",
              errors.medicines?.[index]?.taken_when && "border-destructive/30 bg-destructive/5 focus:border-destructive"
            )}
            showAllOnFocus={true}
            dropdownMinWidth={200}
            ref={(el) => {
              inputRefs.current[index * 6 + 2] = el;
            }}
          />
        );

      case 'duration':
        return (
          <AutocompleteInput
            value={getValues(`medicines.${index}.duration`) || ""}
            onChange={(val) => {
              setValue(`medicines.${index}.duration`, val, { shouldDirty: true });
              handleAutoQuantity(index);
            }}
            options={DURATION_OPTIONS}
            placeholder="5 days"
            onEnterNext={() => focusNextField(index, 4)}
            className={cn(
              "h-7 px-2 text-xs border border-input rounded-md bg-background transition-all duration-200",
              "focus:border-ring focus:outline-none focus-visible:ring-0",
              "hover:border-ring/60 placeholder:text-muted-foreground placeholder:font-normal",
              errors.medicines?.[index]?.duration && "border-destructive/30 bg-destructive/5 focus:border-destructive"
            )}
            showAllOnFocus={true}
            dropdownMinWidth={200}
            ref={(el) => {
              inputRefs.current[index * 6 + 3] = el;
            }}
          />
        );

      case 'quantity': {
        const quantityValue = watch(`medicines.${index}.quantity`) || null;
        return (
            <div className="flex items-center justify-center gap-1">
              <Input
                  type="number"
                  {...register(`medicines.${index}.quantity`)}
                  value={quantityValue}
                  min="0"
                  onChange={(e) => {
                    const value = e.target.value;
                    // Prevent negative values
                    if (value === '' || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
                      setValue(`medicines.${index}.quantity`, value, {
                        shouldValidate: true,
                        shouldDirty: true
                      });
                    }
                  }}
                  className={cn(
                      "h-7 px-2 text-xs border border-input rounded-md bg-background transition-all duration-200",
                      "focus:border-ring focus:outline-none focus-visible:ring-0",
                      "hover:border-ring/60 placeholder:text-muted-foreground placeholder:font-normal",
                      // Hide number input spinners
                      "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                      errors.medicines?.[index]?.quantity && "border-destructive/30 bg-destructive/5 focus:border-destructive"
                  )}
                  placeholder="0"
                  ref={(el) => {
                    inputRefs.current[index * 6 + 4] = el;
                  }}
                  onKeyDown={(e) => {
                    // Prevent minus key
                    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                    if (e.key === "Enter") {
                      e.preventDefault();
                      focusNextField(index, 5);
                    }
                  }}
              />
            </div>
        );
      }

      case 'notes':
        return (
          <Input
            {...register(`medicines.${index}.notes`)}
            placeholder="Instructions..."
            className={cn(
              "h-7 px-2 text-xs border border-input rounded-md bg-background transition-all duration-200",
              "focus:border-ring focus:outline-none focus-visible:ring-0",
              "hover:border-ring/60 placeholder:text-muted-foreground placeholder:font-normal",
              errors.medicines?.[index]?.notes && "border-destructive/30 bg-destructive/5 focus:border-destructive"
            )}
            ref={(el) => {
              inputRefs.current[index * 6 + 5] = el;
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const nextRow = (index + 1) * 6;
                if (inputRefs.current[nextRow]) {
                  inputRefs.current[nextRow]?.focus();
                } else {
                  append(DEFAULT_ROW);
                  setTimeout(() => inputRefs.current[nextRow]?.focus(), 50);
                }
              }
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <FormTable
      title="Medicine"
      columns={MedicineTableColumns}
      fields={fields}
      watched={watched}
      errors={errors}
      renderCell={renderCell}
      onAddRow={() => append(DEFAULT_ROW)}
      onRemoveRow={(index) => remove(index)}
      canRemoveRow={() => fields.length > 1}
      addButtonText="Add Medicine"
      showRowNumbers={true}
      autoAddRow={true}
      primaryField="product"
    />
  );
}